// Daily puzzles: player-local date, loading pre-generated dailies, played/won records on this device, and a
// month calendar of days played. Days in a row are in lib/streak.ts, by the quiet rules
// (.wiki/wiki/concepts/quiet-retention.md): any game counts, a missed day a week is forgiven, a lost run is never shown.
import { readJSON, writeJSON } from "./store"

export type DailyStatus = "played" | "won"
type Records = Record<string, Record<string, DailyStatus>>
const KEY = "dt:daily:v1"

/** Local calendar date as YYYY-MM-DD (the day boundary is the player's own midnight). */
export function localISO(d: Date = new Date()): string {
  const p = (n: number): string => String(n).padStart(2, "0")
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

/** True for the player's own today; false for an archive day, whose win message must not say "today". */
export const isToday = (date: string): boolean => date === localISO()

export function formatLong(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number) as [number, number, number]
  return new Date(y, m - 1, d).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })
}

export async function fetchDaily<T>(game: string, date: string): Promise<T | null> {
  try {
    const res = await fetch(`/daily/${game}/${date}.json`)
    return res.ok ? ((await res.json()) as T) : null
  } catch {
    return null
  }
}

export function records(): Records {
  return readJSON<Records>(KEY) ?? {}
}

export function status(game: string, date: string): DailyStatus | undefined {
  return records()[game]?.[date]
}

export function mark(game: string, date: string, s: DailyStatus): void {
  const r = records()
  const g = (r[game] ??= {})
  if (g[date] === "won") return // never downgrade a win
  if (g[date] === s) return
  g[date] = s
  writeJSON(KEY, r)
  document.dispatchEvent(new CustomEvent("dt:daily", { detail: { game, date, status: s } }))
}

/** Resolve which date a daily page should show: the archive date, or the player's local today. */
export function pageDate(board: HTMLElement): { date: string; archive: boolean } {
  const archive = board.dataset.dailyArchive === "true"
  return { date: archive ? board.dataset.dailyDate! : localISO(), archive }
}

export function updateLabel(date: string): void {
  const label = document.querySelector<HTMLElement>("[data-daily-label]")
  if (label) label.textContent = formatLong(date)
}

/** One day across every game (Today's puzzles): solved if any was solved, played if any was played. */
function dayAcrossGames(iso: string): { st: DailyStatus | undefined; label: string } {
  const all = Object.values(records()).map((g) => g[iso]).filter(Boolean)
  const won = all.filter((s) => s === "won").length
  if (!all.length) return { st: undefined, label: "" }
  const played = `${all.length} ${all.length === 1 ? "puzzle" : "puzzles"} played`
  return { st: won ? "won" : "played", label: won ? `, ${played}, ${won} solved` : `, ${played}` }
}

/** Month calendar: each available past day links to its archive page; played and won days are marked.
 *  With game null it covers every game (Today's puzzles), and days are marks only, not links. */
export function renderCalendar(el: HTMLElement, game: string | null, path: string, today = localISO(), month = today.slice(0, 7)): void {
  const [y, m] = month.split("-").map(Number) as [number, number]
  const first = el.dataset.first ?? "0000-00-00"
  const startDay = new Date(y, m - 1, 1).getDay()
  const daysIn = new Date(y, m, 0).getDate()
  const recs = game ? records()[game] ?? {} : {}
  const p = (n: number): string => String(n).padStart(2, "0")
  const prev = `${m === 1 ? y - 1 : y}-${p(m === 1 ? 12 : m - 1)}`
  const next = `${m === 12 ? y + 1 : y}-${p(m === 12 ? 1 : m + 1)}`
  const title = new Date(y, m - 1, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" })
  let cells = ""
  for (let i = 0; i < startDay; i++) cells += `<td></td>`
  for (let d = 1; d <= daysIn; d++) {
    const iso = `${y}-${p(m)}-${p(d)}`
    const across = game ? null : dayAcrossGames(iso)
    const st = across ? across.st : recs[iso]
    const mark = st === "won" ? "✓" : st === "played" ? "•" : ""
    const label = `${formatLong(iso)}${across ? across.label : st === "won" ? ", solved" : st === "played" ? ", played" : ""}`
    const inner = `<span class="day-num">${d}</span><span class="day-mark" aria-hidden="true">${mark}</span>`
    const playable = game !== null && iso <= today && iso >= first
    cells += `<td class="${st ? `is-${st}` : ""}${iso === today ? " is-today" : ""}">${playable
      ? `<a href="${path}/daily${iso === today ? "" : `/${iso}`}" aria-label="${label}">${inner}</a>`
      : `<span aria-label="${label}">${inner}</span>`}</td>`
    if ((startDay + d) % 7 === 0 && d !== daysIn) cells += "</tr><tr>"
  }
  el.innerHTML =
    `<div class="cal-head"><button type="button" class="btn" data-month="${prev}">Previous month</button>` +
    `<h3 class="cal-title">${title}</h3>` +
    `<button type="button" class="btn" data-month="${next}"${next > today.slice(0, 7) ? " disabled" : ""}>Next month</button></div>` +
    `<table class="cal"><thead><tr>${["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => `<th scope="col">${d}</th>`).join("")}</tr></thead><tbody><tr>${cells}</tr></tbody></table>` +
    `<p class="cal-key"><span aria-hidden="true">✓</span> ${game ? "solved" : "solved one or more"} &nbsp; <span aria-hidden="true">•</span> played</p>`
  el.querySelectorAll<HTMLButtonElement>("[data-month]").forEach((b) =>
    b.addEventListener("click", () => renderCalendar(el, game, path, today, b.dataset.month!)))
}

export function mountCalendar(game: string, path: string): void {
  const el = document.querySelector<HTMLElement>("[data-calendar]")
  if (!el) return
  renderCalendar(el, game, path)
  document.addEventListener("dt:daily", () => renderCalendar(el, game, path))
}
